/* eslint-disable eslint-comments/disable-enable-pair -- kendo button*/

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { checkCircleBrokenLine } from "@tql/ui-components-react/untitled/icons";
import type { SyntheticEvent } from "react";
import { useContext, useState } from "react";

import { ShipmentNotificationModal } from "~/features/notifications/components/shipment-notification-modal/shipment-notification-modal.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { TenderDocumentOptionsFields } from "~/shared/components/tender-document-options-fields/tender-document-options-fields.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";

import type { LabelSize } from "../../constants/tender-modal-constants";
import {
	flaggedLabelSizes,
	labelSizes,
} from "../../constants/tender-modal-constants.ts";

type TenderFormData = {
	labelSize: LabelSize;
	labelQuantity: number;
	additionalDetails: string;
};

const labelSizeValidator = (value: LabelSize | null) => {
	if (!value?.labelSize) {
		return "Size is required";
	}
	return "";
};

const labelQuantityValidator = (value: number | null) => {
	if (!value || value < 1) {
		return "Label quantity must be at least 1";
	}
	if (value > 999) {
		return "Label quantity cannot exceed 999";
	}
	return "";
};

const additionalDetailsValidator = (value?: string) => {
	if (value && value.length > 100) {
		return "Additional details cannot exceed 100 characters";
	}
	return "";
};

type ConfirmationScreenProps = {
	formData: TenderFormData;
	onSendClick: (formData: TenderFormData) => void;
	onDownload: (formData: TenderFormData) => void;
	onView: (formData: TenderFormData) => void;
	isLoading: boolean;
	title?: string;
};

export const ConfirmationScreen = ({
	formData,
	onSendClick,
	onDownload,
	onView,
	isLoading,
	title,
}: ConfirmationScreenProps) => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { isReady, isEnabled } = useFeatureFlagGate("traxLtl4X6Label");
	const { isEnabled: notificationsEnabled } = useFeatureFlagGate(
		"traxLtlNotifications",
	);
	const { isVendor } = useIsVendor();
	const [showNotificationModal, setShowNotificationModal] = useState(false);

	const labelsData: readonly LabelSize[] =
		isReady && isEnabled ? flaggedLabelSizes : labelSizes;

	const handleSubmit = (
		dataItem: Record<string, unknown>,
		event?: SyntheticEvent<unknown, Event>,
	) => {
		if (!event) return;
		const submitter = (event.nativeEvent as SubmitEvent)
			.submitter as HTMLButtonElement;
		const submitterName = submitter?.name;
		if (!submitterName) return;
		switch (submitterName) {
			case "view": {
				onView(dataItem as TenderFormData);
				break;
			}
			case "download": {
				onDownload(dataItem as TenderFormData);
				break;
			}
			case "send": {
				onSendClick(dataItem as TenderFormData);
				break;
			}
			default: {
				break;
			}
		}
	};

	return (
		<Form
			ignoreModified={true}
			initialValues={formData}
			onSubmit={handleSubmit}
			render={(formRenderProps: FormRenderProps) => {
				return (
					<FormElement>
						<div
							className={"tender-modal"}
							data-testid={"shipment-tendered-modal"}
						>
							<div className={"checkmark"}>
								<SvgIcon
									className={"checkmark-svg"}
									icon={checkCircleBrokenLine}
								/>
							</div>
							<div className={"tender-header"}>
								{title ?? "Shipment Tendered!"}
							</div>
							<div className={"message"}>
								Prepare your shipping labels before sending the BOL and labels
								to the shipper
							</div>
							<hr className={"tql-divider"} />
							<div className={"tender-modal-input-group"}>
								<TenderDocumentOptionsFields
									additionalDetailsId={"additionalDetails"}
									additionalDetailsLabel={"Additional Details"}
									additionalDetailsMaxLength={100}
									additionalDetailsName={"additionalDetails"}
									additionalDetailsValidator={additionalDetailsValidator}
									betweenFieldsAndAdditional={
										<>
											<div className={"tender-modal-row"}>
												<Label className={"tender-modal-label"}>BOL#</Label>
												<div className={"tender-modal-input"}>
													{quoteState.poNumber ?? "-"}
												</div>
											</div>
											<div className={"tender-modal-row"}>
												<Label className={"tender-modal-label"}>
													Customer PO#
												</Label>
												<div className={"tender-modal-input"}>
													{quoteState.customerReference || "-"}
												</div>
											</div>
										</>
									}
									classNames={{
										additional: "tender-modal-additional-details",
										additionalHeader: "tender-modal-additional-header",
										characterCount: "tender-modal-character-count",
										input: "tender-modal-input",
										label: "tender-modal-label",
										labelCountInput: "tender-modal-label-quantity",
										row: "tender-modal-row",
										sizeInput: "tender-modal-size",
									}}
									formRenderProps={formRenderProps}
									isLoading={isLoading}
									labelCountId={"labelQuantity"}
									labelCountLabel={"How Many Labels"}
									labelCountName={"labelQuantity"}
									labelCountValidator={labelQuantityValidator}
									labelOptions={labelsData}
									labelSizeId={"labelSize"}
									labelSizeName={"labelSize"}
									labelSizeValidator={labelSizeValidator}
								/>
							</div>
							<div className={"tender-modal-actions"}>
								<div className={"tender-modal-actions-left"}>
									{notificationsEnabled && !isVendor ? (
										<Button
											onClick={() => {
												setShowNotificationModal(true);
											}}
										>
											Subscribe to Notifications
										</Button>
									) : null}
								</div>
								<div className={"tender-modal-actions-right"}>
									<div>
										<Button
											disabled={isLoading}
											formAction={"submit"}
											name={"view"}
										>
											View
										</Button>
									</div>
									<div>
										<Button
											disabled={isLoading}
											formAction={"submit"}
											name={"download"}
										>
											Download
										</Button>
									</div>
									<div>
										<Button
											disabled={isLoading}
											formAction={"submit"}
											name={"send"}
											variant={"primary"}
										>
											Send
										</Button>
									</div>
								</div>
							</div>
							{isVendor ? null : (
								<ShipmentNotificationModal
									customerId={quoteState.customer.id}
									isOpen={showNotificationModal}
									onClose={() => {
										setShowNotificationModal(false);
									}}
									poNumber={Number(quoteState.poNumber)}
								/>
							)}
						</div>
					</FormElement>
				);
			}}
		/>
	);
};
